import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Section, canAccessSection } from "@/app/section-access";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { formatDateShort } from "@/shared/lib/format-date";
import { i18n } from "@/shared/lib/i18n";
import { useOwnerPayouts } from "./use-owner-payouts";
import { useOwnerPayoutProjection } from "./use-owner-payout-projection";
import { OwnerPayoutProjectionCard } from "./OwnerPayoutProjectionCard";
import { OwnerPayoutStatusBadge } from "./OwnerPayoutStatusBadge";
import { formatMoney, moneyFromMajorNumber } from "./money";
import { OWNER_PAYOUTS_LIMIT } from "./constants";
import { OWNER_PAYOUT_STATUSES, type OwnerPayoutStatus } from "./types";

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

export function OwnerPayoutsPage() {
  const { t } = useTranslation();
  const { userId = "" } = useParams<{ userId: string }>();
  const [status, setStatus] = useState<OwnerPayoutStatus | null>(null);
  // FINANCE reaches this page from the payout report and may not open the
  // user page (`SECTION_ROLES.users`), so Back goes where the viewer can go.
  const canOpenUser = canAccessSection(useCurrentUser()?.role, Section.USERS);
  const backTo = canOpenUser ? `/users/${userId}` : "/finance/payouts";

  const projection = useOwnerPayoutProjection(userId);
  const { data, isLoading, isFetching, isError } = useOwnerPayouts(userId, {
    status,
    limit: OWNER_PAYOUTS_LIMIT,
  });
  const payouts = data?.payouts ?? [];

  return (
    <div data-testid="owner-payouts-screen" className="space-y-6">
      <div>
        <Link
          to={backTo}
          data-testid="owner-payouts-back"
          className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("finance.ownerPayouts.back")}
        </Link>
      </div>

      <header className="space-y-2">
        <p className="brand-eyebrow">{t("finance.ownerPayouts.eyebrow")}</p>
        <h1 className="text-3xl">{t("finance.ownerPayouts.title")}</h1>
        <p className="font-sans text-base font-normal text-muted-foreground">
          {t("finance.ownerPayouts.subtitle")}
        </p>
      </header>

      {projection.isLoading && (
        <p
          data-testid="owner-payout-projection-loading"
          className="font-sans text-sm text-muted-foreground"
        >
          {t("finance.ownerPayouts.projection.loading")}
        </p>
      )}

      {projection.isError && (
        <p
          data-testid="owner-payout-projection-error"
          className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 font-sans text-sm text-amber-700"
        >
          {t("finance.ownerPayouts.projection.unavailable")}
        </p>
      )}

      {projection.data && (
        <OwnerPayoutProjectionCard projection={projection.data} locale={i18n.language} />
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1.5">
          <label
            htmlFor="owner-payout-status"
            className="font-sans text-sm font-medium text-foreground"
          >
            {t("finance.ownerPayouts.filters.status")}
          </label>
          <select
            id="owner-payout-status"
            data-testid="filter-payout-status"
            value={status ?? ""}
            onChange={(e) =>
              setStatus(e.target.value === "" ? null : (e.target.value as OwnerPayoutStatus))
            }
            className={SELECT_CLASS}
          >
            <option value="">{t("finance.ownerPayouts.filters.statusAll")}</option>
            {OWNER_PAYOUT_STATUSES.map((value) => (
              <option key={value} value={value}>
                {t(`finance.ownerPayouts.status.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {isLoading && (
        <p data-testid="owner-payouts-loading" className="font-sans text-sm text-muted-foreground">
          {t("finance.ownerPayouts.loading")}
        </p>
      )}

      {isError && (
        <div
          data-testid="owner-payouts-error"
          className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center"
        >
          <p className="font-sans text-sm font-medium text-destructive">
            {t("finance.ownerPayouts.error")}
          </p>
        </div>
      )}

      {!isLoading && !isError && payouts.length === 0 && (
        <div
          data-testid="owner-payouts-empty"
          className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center"
        >
          <p className="font-sans text-sm font-medium text-foreground">
            {status ? t("finance.ownerPayouts.emptyFiltered") : t("finance.ownerPayouts.empty")}
          </p>
        </div>
      )}

      {!isError && payouts.length > 0 && (
        <ul
          data-testid="owner-payouts-list"
          aria-busy={isFetching}
          className={isFetching ? "space-y-2 opacity-70 transition-opacity" : "space-y-2"}
        >
          {payouts.map((payout) => (
            <li
              key={payout.id}
              data-testid={`owner-payout-row-${payout.id}`}
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
            >
              <div className="space-y-0.5">
                <p className="font-mono text-sm font-medium text-foreground">
                  {formatMoney(moneyFromMajorNumber(payout.amount, payout.currency))}{" "}
                  <span className="uppercase text-muted-foreground">{payout.currency}</span>
                </p>
                <p className="font-sans text-xs text-muted-foreground">
                  {payout.venueName ?? payout.venueId}
                  {" · "}
                  {t("finance.ownerPayouts.period", {
                    from: formatDateShort(payout.periodStart, i18n.language),
                    to: formatDateShort(payout.periodEnd, i18n.language),
                    count: payout.checkinCount,
                  })}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {payout.paidAt && (
                  <span className="font-sans text-xs text-muted-foreground">
                    {t("finance.ownerPayouts.paidAt", {
                      date: formatDateShort(payout.paidAt, i18n.language),
                    })}
                  </span>
                )}
                <OwnerPayoutStatusBadge
                  status={payout.status}
                  testId={`owner-payout-status-${payout.id}`}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
